import React, { lazy, Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { VisualFrame } from './VisualFrame';
// import { MermaidRenderer } from './MermaidRenderer'; // COMMENTED OUT: Replaced with Three.js 2D
import { ChartRenderer } from './ChartRenderer';
import { MapRenderer } from './MapRenderer';
import { CircuitRenderer } from './CircuitRenderer';
import { DiagramRenderer } from './DiagramRenderer';
import { ThreeJsRenderer } from './ThreeJsRenderer';

// Lazy load heavy renderers for better performance
const MoleculeRenderer = lazy(() => import('./MoleculeRenderer'));
const MusicRenderer = lazy(() => import('./MusicRenderer'));
const GraphRenderer = lazy(() => import('./GraphRenderer'));

/**
 * Main router component for all visual types
 * Detects the visual type and routes to appropriate renderer
 */
export function VisualRenderer({ language, code, collapsed = false }) {
  const renderVisual = () => {
    try {
      switch (language) {
        // COMMENTED OUT: Mermaid case - now using Three.js for diagrams
        // case 'mermaid':
        //   return <MermaidRenderer code={code} />;

        case 'chart':
          const chartConfig = JSON.parse(code);
          return <ChartRenderer config={chartConfig} />;

        case 'molecule':
          const moleculeData = JSON.parse(code);
          return (
            <Suspense fallback={<LoadingSkeleton type="3D molecule" />}>
              <MoleculeRenderer data={moleculeData} />
            </Suspense>
          );

        case 'map':
          const mapData = JSON.parse(code);
          return <MapRenderer data={mapData} />;

        case 'music':
          const musicData = JSON.parse(code);
          return (
            <Suspense fallback={<LoadingSkeleton type="music notation" />}>
              <MusicRenderer data={musicData} />
            </Suspense>
          );

        case 'circuit':
          const circuitData = JSON.parse(code);
          return <CircuitRenderer data={circuitData} />;

        case 'diagram':
          const diagramData = JSON.parse(code);
          return <DiagramRenderer data={diagramData} />;

        case 'graph':
          const graphData = JSON.parse(code);
          return (
            <Suspense fallback={<LoadingSkeleton type="graph" />}>
              <GraphRenderer data={graphData} />
            </Suspense>
          );

        case 'threejs':
          return <ThreeJsRenderer code={code} />;

        default:
          return (
            <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-x-auto">
              <code>{code}</code>
            </pre>
          );
      }
    } catch (error) {
      return (
        <div className="text-red-600 dark:text-red-400 p-4 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
          <strong>Visual Error:</strong> {error.message}
          <details className="mt-2 text-sm">
            <summary className="cursor-pointer">Details</summary>
            <pre className="mt-2 whitespace-pre-wrap">{code}</pre>
          </details>
        </div>
      );
    }
  };

  const getVisualTitle = (lang) => {
    const titles = {
      chart: 'Data Visualization',
      molecule: '3D Molecular Structure',
      map: 'Geographic Map',
      music: 'Music Notation',
      circuit: 'Circuit Diagram',
      diagram: 'Interactive Diagram',
      graph: 'Mathematical Graph',
      // mermaid: 'Diagram', // COMMENTED OUT: Replaced with Three.js
      threejs: 'Interactive Visual',
    };
    return titles[lang] || lang;
  };

  const ErrorFallback = ({ error }) => (
    <div className="text-red-600 dark:text-red-400 p-4 bg-red-50 dark:bg-red-900/20 rounded">
      <strong>Unable to render visual:</strong> {error.message}
    </div>
  );

  const getDescription = (lang) => {
    if (lang === 'threejs') {
      return 'Interact with this visualization to explore the concept';
    }
    // Mermaid removed - now using Three.js for all diagrams
    // if (lang === 'mermaid') {
    //   return 'Visual diagram to help understand the concept';
    // }
    return undefined;
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <VisualFrame
        title={getVisualTitle(language)}
        type={language}
        description={getDescription(language)}
        defaultCollapsed={collapsed}
      >
        {renderVisual()}
      </VisualFrame>
    </ErrorBoundary>
  );
}

/**
 * Loading skeleton for lazy-loaded components
 */
function LoadingSkeleton({ type }) {
  return (
    <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-800 rounded animate-pulse">
      <div className="text-center">
        <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin mb-2" />
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading {type}...</p>
      </div>
    </div>
  );
}
